import { isAfter, isBefore } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { revertTransformDate, CET } from '@lib/utils/dateTimeHelpers';

interface WidgetPeriod {
  startAt?: string | null;
  endAt?: string | null;
}

/**
 * Checks if now is after or equal to the given date
 */
function isAfterOrEqual(date: Date, dateToCompare: Date): boolean {
  return !isBefore(date, dateToCompare);
}

/**
 * Checks if now is before or equal to the given date
 */
function isBeforeOrEqual(date: Date, dateToCompare: Date): boolean {
  return !isAfter(date, dateToCompare);
}

/**
 * Checks if a widget is visible based on its visibility period.
 * A widget is visible if:
 * - No period is defined (both startAt and endAt are null/undefined)
 * - Current time is within the defined period (inclusive)
 *
 * Uses CET timezone for consistent date comparison across the app.
 *
 * @param period - Object containing startAt and endAt dates (ISO strings)
 * @returns true if the widget should be visible, false otherwise
 */
export default function isWidgetVisibleByPeriod(period: WidgetPeriod): boolean {
  const { startAt, endAt } = period;

  // If no period is defined, widget is always visible
  if (!startAt && !endAt) {
    return true;
  }

  // Get current time in CET timezone for consistent comparison
  const now = toZonedTime(new Date(), CET);

  // If only startAt is defined, check if current time is after or equal to startAt
  if (startAt && !endAt) {
    const startDate = revertTransformDate(startAt);
    return isAfterOrEqual(now, startDate);
  }

  // If only endAt is defined, check if current time is before or equal to endAt
  if (!startAt && endAt) {
    const endDate = revertTransformDate(endAt);
    return isBeforeOrEqual(now, endDate);
  }

  // Both startAt and endAt are defined
  if (startAt && endAt) {
    const startDate = revertTransformDate(startAt);
    const endDate = revertTransformDate(endAt);

    return isAfterOrEqual(now, startDate) && isBeforeOrEqual(now, endDate);
  }

  return true;
}
